import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { CambioDePrecio } from '@/components/ui/precio';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad, formatoFechaBolivia, formatoPrecio } from '@/lib/utils/formato-precio';
import { empezarCambioPropio } from '@/features/smart-pricing/lib/cambio-propio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { RecalculoEnPantalla, VistaPreviaDelRecalculo } from '../types/pricing-rules.types';
import {
  COMO_RECALCULA,
  cambiosEnTexto,
  encabezadoDeLaVistaPrevia,
  filtrarCandidatos,
  losQueNoVuelvenEnPalabras,
  nadaSeAplicaSolo,
  porQueEnTexto,
  queHaceElCambio,
  quienLoPropone,
  repartoEnPalabras,
  type FiltroDelRecalculo,
} from '../lib/recalculo';
import { EncabezadoDeVentana, PIE_DE_VENTANA } from '@/features/compite/ui/ventana-compite';

/**
 * «Aplicar las reglas y recalcular» (lote C1, spec 246 punto 8; maqueta
 * #c1-recalcular): una ventana de dos pasos. Primero la vista previa —a cuántos
 * les cambia el precio, con y sin estrategia, y por qué—, que no mueve nada;
 * después «Enviar a Por decidir», que tampoco aplica nada: todo va a la bandeja
 * con el origen «recálculo».
 */

/** Cuántas filas se ven en la tabla de la vista previa: en Por decidir están todas. */
const FILAS_A_LA_VISTA = 100;

type Paso =
  | { tipo: 'CALCULANDO' }
  | { tipo: 'ERROR' }
  | { tipo: 'VISTA'; vista: VistaPreviaDelRecalculo }
  | { tipo: 'MANDANDO'; id: string; avance: RecalculoEnPantalla | null }
  | { tipo: 'LISTO'; r: RecalculoEnPantalla }
  | { tipo: 'FALLO'; mensaje: string };

/** El id del recálculo en curso que trae un 409 (`data.id`), o null. */
function enCursoDe(e: unknown): string | null {
  const r = (e as { response?: { status?: number; data?: { data?: { id?: unknown } } } })?.response;
  const id = r?.status === 409 ? r.data?.data?.id : null;
  return typeof id === 'string' ? id : null;
}

export function RecalcularDialog({
  abierto,
  puedeMandar,
  onCerrar,
  onIrAPorDecidir,
  onTermino,
  onAplicado,
  esperaEntreConsultasMs = 1000,
}: {
  abierto: boolean;
  /** Mandar a Por decidir pide el permiso de encender reglas; mirar, sólo ver. */
  puedeMandar: boolean;
  onCerrar: () => void;
  /** «Ir a Por decidir», con «Origen: recálculo». */
  onIrAPorDecidir: () => void;
  /** Avisa que terminó (para que Reglas apague su aviso). */
  onTermino?: () => void;
  /**
   * Ronda de la T11 (I1): el servidor aceptó el envío (202): los cambios sin
   * aplicar ya se aplicaron (se promueven antes de responder), aunque el
   * recálculo siga. Reglas relee la lista y el aviso (si no, el aviso al salir
   * diría «se descartan» de cambios que ya rigen).
   */
  onAplicado?: () => void;
  esperaEntreConsultasMs?: number;
}) {
  const [paso, setPaso] = useState<Paso>({ tipo: 'CALCULANDO' });
  const [filtro, setFiltro] = useState<FiltroDelRecalculo>('TODOS');
  // Spec 294 (D-R4-4): un 409 de «otro recálculo en curso» no aplicó nada: el
  // seguimiento de ése lo dice, si había cambios para aplicar.
  const [noSeAplicaron, setNoSeAplicaron] = useState(false);

  const calcular = useCallback(() => {
    setPaso({ tipo: 'CALCULANDO' });
    setFiltro('TODOS');
    setNoSeAplicaron(false);
    pricingRulesApi
      .vistaPreviaDelRecalculo()
      .then((vista) => setPaso({ tipo: 'VISTA', vista }))
      .catch(() => setPaso({ tipo: 'ERROR' }));
  }, []);

  useEffect(() => {
    if (abierto) calcular();
  }, [abierto, calcular]);

  // Mientras se manda, se pregunta cómo va hasta que termina (o falla).
  const idMandando = paso.tipo === 'MANDANDO' ? paso.id : null;
  useEffect(() => {
    if (!idMandando) return;
    let vigente = true;
    let t: ReturnType<typeof setTimeout> | undefined;
    // Ronda final C1: si la consulta del avance siempre falla, no se pregunta
    // para siempre: al quinto error seguido se da por perdido.
    let erroresSeguidos = 0;
    const preguntar = () => {
      pricingRulesApi
        .recalculo(idMandando)
        .then((r) => {
          if (!vigente) return;
          erroresSeguidos = 0;
          if (r.estado === 'LISTO') {
            setPaso({ tipo: 'LISTO', r });
            onTermino?.();
          } else if (r.estado === 'FALLO') {
            setPaso({ tipo: 'FALLO', mensaje: r.error ?? 'No se pudo calcular.' });
            onTermino?.();
          } else {
            setPaso({ tipo: 'MANDANDO', id: idMandando, avance: r });
            t = setTimeout(preguntar, esperaEntreConsultasMs);
          }
        })
        .catch(() => {
          if (!vigente) return;
          erroresSeguidos++;
          if (erroresSeguidos >= 5) {
            setPaso({
              tipo: 'FALLO',
              mensaje: 'No sabemos cómo va el envío: mirá Por decidir en unos minutos.',
            });
            return;
          }
          t = setTimeout(preguntar, esperaEntreConsultasMs);
        });
    };
    preguntar();
    return () => {
      vigente = false;
      clearTimeout(t);
    };
    // `onTermino` puede cambiar en cada render del padre: no reinicia la consulta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idMandando, esperaEntreConsultasMs]);

  // Ronda de R3: la acción propia empieza en el clic (antes del 202). Con qué
  // terminarla, hasta que la toma el seguimiento o el envío falla.
  const envio = useRef<(() => void) | null>(null);
  const soltarEnvio = () => {
    envio.current?.();
    envio.current = null;
  };
  // Si la ventana se desmonta con el envío sin respuesta, la marca no queda puesta.
  useEffect(() => () => soltarEnvio(), []);

  // Spec 275 (R3, I13): el recálculo propio escribe después del 202: mientras se
  // manda es una acción propia (la franja «Hay cambios nuevos» no se pide), y al
  // terminar (o al cerrar la ventana) vuelve a tomar la firma.
  useEffect(() => {
    if (paso.tipo !== 'MANDANDO') return;
    const terminar = empezarCambioPropio();
    // Ronda de R3: el seguimiento toma la marca del clic sin hueco entre las dos.
    soltarEnvio();
    return terminar;
  }, [paso.tipo]);

  const mandar = () => {
    soltarEnvio();
    envio.current = empezarCambioPropio();
    // Spec 294 (D-R4-4): la firma de lo que se vio va SIEMPRE, también vacía: si
    // alguien escribió un cambio mientras se miraba, el servidor no aplica nada (409).
    const firma = paso.tipo === 'VISTA' ? (paso.vista.firmaDeBorradores ?? '') : '';
    const habiaCambios = paso.tipo === 'VISTA' && (paso.vista.cambiosSinAplicar?.length ?? 0) > 0;
    pricingRulesApi
      .mandarRecalculo(firma)
      .then(({ id }) => {
        setPaso({ tipo: 'MANDANDO', id, avance: null });
        onAplicado?.();
      })
      .catch((e) => {
        const enCurso = enCursoDe(e);
        if (enCurso) {
          setNoSeAplicaron(habiaCambios);
          setPaso({ tipo: 'MANDANDO', id: enCurso, avance: null });
        } else {
          soltarEnvio();
          setPaso({
            tipo: 'FALLO',
            // Spec 275 (R3): un 409 sin id es «hay una corrida en curso»: su texto.
            mensaje: mensajeDeError(
              e,
              'No se pudo enviar: no se movió nada ni llegó nada a Por decidir.'
            ),
          });
        }
      });
  };

  const vista = paso.tipo === 'VISTA' ? paso.vista : null;
  const total = vista ? vista.candidatos.length + vista.recortados : 0;
  // Spec 294 (D-R4-4): con cambios sin aplicar, aplicar es un solo botón, aunque
  // no muevan ningún precio (si no, no habría cómo aplicarlos).
  const cambiosAAplicar = vista?.cambiosSinAplicar?.length ?? 0;
  const filas = vista ? filtrarCandidatos(vista.candidatos, filtro) : [];

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col overflow-hidden">
        <EncabezadoDeVentana icono={RefreshCw}>
          <DialogTitle>Aplicar las reglas y recalcular</DialogTitle>
          <DialogDescription>
            {vista
              ? encabezadoDeLaVistaPrevia(vista)
              : 'Todo el catálogo, con y sin estrategia, con las reglas encendidas.'}
          </DialogDescription>
          <ol className="flex gap-4 pt-1 text-xs text-muted-foreground" aria-label="Pasos">
            <li
              aria-current={paso.tipo !== 'LISTO' && paso.tipo !== 'MANDANDO' ? 'step' : undefined}
              className={cn(
                paso.tipo !== 'LISTO' && paso.tipo !== 'MANDANDO' && 'font-semibold text-foreground'
              )}
            >
              1 · Ver a cuántos les cambia
            </li>
            <li
              aria-current={paso.tipo === 'LISTO' || paso.tipo === 'MANDANDO' ? 'step' : undefined}
              className={cn(
                (paso.tipo === 'LISTO' || paso.tipo === 'MANDANDO') &&
                  'font-semibold text-foreground'
              )}
            >
              {cambiosAAplicar > 0
                ? '2 · Aplicar y enviar a Por decidir'
                : '2 · Enviarlos a Por decidir'}
            </li>
          </ol>
          <p data-testid="como-recalcula" className="pt-1 text-xs text-muted-foreground">
            {COMO_RECALCULA}
          </p>
        </EncabezadoDeVentana>

        <div className="-mx-6 min-h-0 flex-1 space-y-3 overflow-y-auto px-6">
          {paso.tipo === 'CALCULANDO' && (
            <div className="space-y-2" data-testid="calculando-recalculo">
              <p className="text-sm">
                Calculando todo el catálogo. Tarda unos segundos. Mirar esto no mueve ningún precio.
              </p>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}

          {paso.tipo === 'ERROR' && (
            <EstadoError onReintentar={calcular}>
              No se pudo calcular: no se movió nada ni llegó nada a Por decidir.
            </EstadoError>
          )}

          {vista && cambiosAAplicar > 0 && (
            <div className="space-y-1 rounded-lg border bg-card p-3" data-testid="lo-que-se-aplica">
              <p className="text-sm font-semibold">Lo que se va a aplicar</p>
              <ul className="space-y-0.5 text-sm">
                {vista.cambiosSinAplicar!.map((c) => (
                  <li key={c.ruleId} className="min-w-0 [overflow-wrap:anywhere]">
                    «{c.nombre}»: {queHaceElCambio(c)}
                    {c.escribieron.length > 0 && (
                      <span className="text-muted-foreground">
                        {' '}
                        · escrito por {c.escribieron.join(', ')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vista && total === 0 && (
            <EstadoVacio
              titulo={
                cambiosAAplicar > 0
                  ? 'Con las reglas como quedan, a ningún producto le cambia el precio.'
                  : 'Con tus reglas de hoy, a ningún producto le cambia el precio.'
              }
            >
              <p>No hay nada que enviar a Por decidir. {repartoEnPalabras(vista.resumen)}</p>
              {losQueNoVuelvenEnPalabras(vista.resumen) !== '' && (
                <p>{losQueNoVuelvenEnPalabras(vista.resumen)}</p>
              )}
            </EstadoVacio>
          )}

          {vista && total > 0 && (
            <>
              <Cifras>
                <Cifra
                  chica
                  valor={vista.resumen.cambian}
                  rotulo={
                    vista.resumen.cambian === 1 ? 'le cambia el precio' : 'les cambia el precio'
                  }
                />
                <Cifra
                  chica
                  valor={vista.resumen.suben}
                  rotulo={vista.resumen.suben === 1 ? 'sube' : 'suben'}
                />
                <Cifra
                  chica
                  valor={vista.resumen.bajan}
                  rotulo={vista.resumen.bajan === 1 ? 'baja' : 'bajan'}
                />
                <Cifra
                  chica
                  valor={vista.resumen.conflicto}
                  tono={vista.resumen.conflicto > 0 ? 'bloqueo' : 'neutro'}
                  rotulo={
                    vista.resumen.conflicto === 1
                      ? 'conflicto entre reglas: no se mueve, te espera'
                      : 'conflicto entre reglas: no se mueven, te esperan'
                  }
                />
              </Cifras>
              <p className="text-sm text-muted-foreground">{repartoEnPalabras(vista.resumen)}</p>

              <GrupoDeOpciones
                aria-label="Cuáles ver"
                valor={filtro}
                onCambiar={setFiltro}
                opciones={[
                  { valor: 'TODOS', texto: 'Todos', cuenta: vista.candidatos.length },
                  {
                    valor: 'CON_ESTRATEGIA',
                    texto: 'Con estrategia',
                    cuenta: filtrarCandidatos(vista.candidatos, 'CON_ESTRATEGIA').length,
                  },
                  {
                    valor: 'SIN_ESTRATEGIA',
                    texto: 'Sin estrategia',
                    cuenta: filtrarCandidatos(vista.candidatos, 'SIN_ESTRATEGIA').length,
                  },
                  {
                    valor: 'CONFLICTO',
                    texto: 'Conflicto entre reglas',
                    cuenta: filtrarCandidatos(vista.candidatos, 'CONFLICTO').length,
                  },
                ]}
              />

              <div className="overflow-hidden rounded-lg border bg-card">
                {/* Ola final R1: table-fixed + colgroup: las columnas suman el
                    ancho del diálogo (medido: la tabla se pasaba 16,7 px y «Cambio»
                    quedaba cortada con scroll horizontal) y el texto largo corta
                    en su celda en vez de empujar la tabla. */}
                <Table className="table-fixed">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[26%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[18%]" />
                  </colgroup>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Por qué cambia</TableHead>
                      <TableHead className="text-right">Hoy (Bs)</TableHead>
                      <TableHead className="text-right">Quedaría (Bs)</TableHead>
                      <TableHead className="text-right">Cambio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filas.slice(0, FILAS_A_LA_VISTA).map((c) => (
                      <TableRow key={c.productId}>
                        <TableCell className="align-top">
                          <div className="min-w-0 font-medium [overflow-wrap:anywhere]">
                            {c.name}
                          </div>
                          <div className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                            {[c.category, c.brand].filter(Boolean).join(' · ') || c.sku}
                          </div>
                        </TableCell>
                        <TableCell className="align-top text-sm">
                          <div
                            className={cn(
                              'min-w-0 font-medium [overflow-wrap:anywhere]',
                              c.conflicto && 'text-bloqueo'
                            )}
                          >
                            {quienLoPropone(c)}
                          </div>
                          <div className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                            {porQueEnTexto(c.porQue)}
                          </div>
                        </TableCell>
                        <TableCell className="align-top text-right tabular-nums">
                          {formatoPrecio(c.hoy)}
                        </TableCell>
                        <TableCell className="align-top text-right font-semibold tabular-nums">
                          {c.conflicto ? (
                            <span className="font-normal text-muted-foreground">no se mueve</span>
                          ) : (
                            formatoPrecio(c.queda)
                          )}
                        </TableCell>
                        <TableCell className="align-top text-right">
                          {c.conflicto ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <CambioDePrecio
                              className="whitespace-normal [overflow-wrap:anywhere]"
                              diferencia={Math.round((c.queda - c.hoy) * 100) / 100}
                              porcentaje={((c.queda - c.hoy) / c.hoy) * 100}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {filas.length > FILAS_A_LA_VISTA && (
                <p className="text-xs text-muted-foreground">
                  Se ven los primeros {FILAS_A_LA_VISTA} de {formatoCantidad(filas.length)}: en Por
                  decidir van a estar todos.
                </p>
              )}

              <Aviso tono="info" titulo="Nada se aplica solo">
                {nadaSeAplicaSolo(vista.resumen, total)}
              </Aviso>
            </>
          )}

          {paso.tipo === 'MANDANDO' && (
            <div
              className="space-y-2"
              data-testid="mandando-recalculo"
              role="status"
              aria-live="polite"
            >
              <p className="text-sm">
                Enviando a Por decidir
                {paso.avance && paso.avance.total > 0
                  ? `: ${formatoCantidad(paso.avance.escritas)} de ${formatoCantidad(paso.avance.total)}`
                  : '…'}
              </p>
              <p className="text-xs text-muted-foreground">
                Podés cerrar: sigue enviándose y llega a Por decidir igual.
              </p>
              {noSeAplicaron && (
                <p className="text-sm">
                  Tus cambios todavía no se aplicaron: cuando termine, aplicalos de nuevo.
                </p>
              )}
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {paso.tipo === 'LISTO' && (
            <Aviso
              tono="ok"
              role="status"
              aria-live="polite"
              titulo={
                paso.r.escritas === 1
                  ? '1 llegó a Por decidir'
                  : `${formatoCantidad(paso.r.escritas)} llegaron a Por decidir`
              }
            >
              Del recálculo del {formatoFechaBolivia(paso.r.creadoEn, { hora: true })} (hora de
              Bolivia). Las vas a ver en Por decidir; en el detalle de cada una dice «Recalculada el
              …».
              {noSeAplicaron && (
                <span className="mt-1 block">
                  Tus cambios todavía no se aplicaron: cuando termine, aplicalos de nuevo.
                </span>
              )}
            </Aviso>
          )}

          {paso.tipo === 'FALLO' && (
            <Aviso tono="bloqueo" role="alert" titulo="No se pudo enviar a Por decidir">
              {paso.mensaje}
            </Aviso>
          )}
        </div>

        <DialogFooter className={cn(PIE_DE_VENTANA, 'sm:justify-between')}>
          <span className="text-xs text-muted-foreground">
            {vista && `Hora de Bolivia · ${formatoFechaBolivia(vista.calculadoEn, { hora: true })}`}
          </span>
          <div className="flex gap-2">
            {paso.tipo === 'LISTO' ? (
              <>
                <Button variant="outline" onClick={onCerrar}>
                  Cerrar
                </Button>
                <Button
                  onClick={() => {
                    onIrAPorDecidir();
                    onCerrar();
                  }}
                >
                  Ir a Por decidir
                </Button>
              </>
            ) : paso.tipo === 'FALLO' ? (
              <>
                <Button variant="outline" onClick={onCerrar}>
                  Cerrar
                </Button>
                <Button onClick={calcular}>Reintentar</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onCerrar}>
                  {paso.tipo === 'MANDANDO' ? 'Cerrar' : 'Cancelar'}
                </Button>
                {vista && puedeMandar && (total > 0 || cambiosAAplicar > 0) && (
                  <Button onClick={mandar}>
                    {cambiosAAplicar > 0
                      ? total > 0
                        ? `Aplicar ${cambiosEnTexto(cambiosAAplicar)} y enviar ${formatoCantidad(total)} a Por decidir`
                        : `Aplicar ${cambiosEnTexto(cambiosAAplicar)}`
                      : `Enviar ${formatoCantidad(total)} a Por decidir`}
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogFooter>
        {vista && !puedeMandar && (cambiosAAplicar > 0 || total > 0) && (
          <p className="text-xs text-muted-foreground">
            {cambiosAAplicar > 0
              ? 'Aplicarlos y enviarlos a Por decidir lo hace quien puede encender reglas.'
              : 'Enviarlos a Por decidir lo hace quien puede encender reglas.'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
